import type { SaleskoOAuthProvider } from "@salesko/contracts";
import { Apple, CreditCard, LogOut, Settings, ShieldCheck, UserRound, type LucideIcon } from "lucide-react";
import { GoogleGlyph } from "../../components/GoogleGlyph";
import { useI18n } from "../../i18n";
import type { StoredSaleskoSession } from "../../lib/api";

const providerMeta = {
  google: { icon: UserRound, label: "Google" },
  apple: { icon: Apple, label: "Apple" },
  linkedin: { icon: UserRound, label: "LinkedIn" }
} satisfies Record<SaleskoOAuthProvider, { icon: LucideIcon; label: string }>;

type AccountSurfaceProps = {
  availableAuthProviders?: SaleskoOAuthProvider[];
  className?: string;
  onOpenBilling?: () => void;
  onOpenSettings: () => void;
  onSignIn: (provider: SaleskoOAuthProvider) => void;
  onSignOut: () => void;
  session: StoredSaleskoSession | null;
  variant?: "rail" | "mobile";
};

export function AccountSurface({
  availableAuthProviders = [],
  className,
  onOpenBilling,
  onOpenSettings,
  onSignIn,
  onSignOut,
  session,
  variant = "rail"
}: AccountSurfaceProps) {
  const { t } = useI18n();
  const classNames = ["account-surface", `account-surface-${variant}`, session ? "is-signed-in" : "is-signed-out", className]
    .filter(Boolean)
    .join(" ");
  const displayName = session?.name ?? session?.email ?? t.common.account;
  const displaySub = session
    ? [session.email, t.accountSurface.tenantLabel(shortScopeId(session.tenantId))].filter(Boolean).join(" · ")
    : t.accountSurface.syncCopy;
  const initials = initialsFor(displayName);

  return (
    <section className={classNames} aria-label={t.accountSurface.ariaLabel}>
      <div className="account-surface-head">
        <span className="account-avatar" aria-hidden="true">
          {session ? initials : <UserRound size={18} />}
        </span>
        <div className="account-copy">
          <p className="eyebrow">{t.common.account}</p>
          <strong>{session ? displayName : t.common.signIn}</strong>
          <span>{displaySub}</span>
        </div>
      </div>

      {session ? (
        <div className="account-actions">
          <button className="secondary-button account-action-button" type="button" onClick={onOpenSettings}>
            <Settings size={15} />
            <span>{t.common.settings}</span>
          </button>
          {onOpenBilling ? (
            <button className="secondary-button account-action-button" type="button" onClick={onOpenBilling}>
              <CreditCard size={15} />
              <span>{t.billing.viewPlans}</span>
            </button>
          ) : null}
          <button className="secondary-button account-action-button" type="button" onClick={onSignOut}>
            <LogOut size={15} />
            <span>{t.common.signOut}</span>
          </button>
        </div>
      ) : (
        <div className="account-actions">
          {availableAuthProviders.length > 0 ? (
            availableAuthProviders.map((provider) => {
              const meta = providerMeta[provider];
              const Icon = meta.icon;
              return (
                <button
                  className="secondary-button account-action-button account-signin-button"
                  key={provider}
                  onClick={() => onSignIn(provider)}
                  title={t.common.signInWith(meta.label)}
                  type="button"
                >
                  {provider === "google" ? <GoogleGlyph size={15} /> : <Icon size={15} />}
                  <span>{t.common.signInWith(meta.label)}</span>
                </button>
              );
            })
          ) : (
            <p className="account-muted-line">{t.accountSurface.noProviderConfigured}</p>
          )}
          {onOpenBilling ? (
            <button className="secondary-button account-action-button" type="button" onClick={onOpenBilling}>
              <CreditCard size={15} />
              <span>{t.billing.viewPlans}</span>
            </button>
          ) : null}
        </div>
      )}

      {session ? (
        <p className="account-status">
          <ShieldCheck size={14} />
          <span>{t.accountSurface.sessionGrantReady}</span>
        </p>
      ) : null}
    </section>
  );
}

export function initialsFor(value: string) {
  const letters = value
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  return letters.slice(0, 2) || "SK";
}

function shortScopeId(value: string) {
  if (value.length <= 14) {
    return value;
  }
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}
